/**
 * The system chrome: keyboard, status bar, splash screen, screen orientation.
 * All four are native-only; on the web every call is a documented no-op,
 * because a browser owns its own chrome.
 */
// `@capacitor/core` is the one Capacitor import allowed at module scope here
// (see `platform.ts`); `SystemBars` lives in it, so the bars need no plugin.
import { SystemBars, SystemBarsStyle } from "@capacitor/core";
import {
  isNative,
  noopUnsubscribe,
  toUnsubscribe,
  type Unsubscribe,
} from "./platform";

/**
 * The software keyboard. `height` is in CSS pixels and is `0` on hide. Web:
 * a no-op — watch `visualViewport` instead, which is what actually changes.
 */
export function onKeyboardChange(
  handler: (height: number) => void
): Unsubscribe {
  if (!isNative()) {
    return noopUnsubscribe;
  }
  const registration = import("@capacitor/keyboard").then(
    async ({ Keyboard }) => {
      const shown = await Keyboard.addListener("keyboardDidShow", (info) =>
        handler(info.keyboardHeight)
      );
      const hidden = await Keyboard.addListener("keyboardDidHide", () =>
        handler(0)
      );
      return {
        async remove() {
          await Promise.all([shown.remove(), hidden.remove()]);
        },
      };
    }
  );
  return toUnsubscribe(registration);
}

/** Web: blurs the focused element, which closes a mobile browser's keyboard. */
export async function hideKeyboard(): Promise<void> {
  if (!isNative()) {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    return;
  }
  const { Keyboard } = await import("@capacitor/keyboard");
  await Keyboard.hide();
}

/**
 * Which theme the system bars' icons must be legible on. `"light"` means a
 * light background, so dark icons. Web: a no-op.
 *
 * BOTH plugins are set, to the same style, on purpose. `@capacitor/status-bar`
 * and the core `SystemBars` each remember their own style and each re-apply it
 * on a configuration change, and the manifest routes `uiMode` changes to the
 * running activity — so leaving either at its default means the next
 * dark-mode toggle of the PHONE resets the bar the app had just set. The core
 * plugin is called with no `bar`, which is status AND navigation: the
 * navigation bar is the half `@capacitor/status-bar` cannot reach at all.
 */
export async function setStatusBarTheme(
  background: "dark" | "light"
): Promise<void> {
  if (!isNative()) {
    return;
  }
  const { StatusBar, Style } = await import("@capacitor/status-bar");
  // Both plugins' names describe the TEXT: `Light` is dark text for a light
  // background, which is the inversion this wrapper exists to hide.
  await Promise.all([
    StatusBar.setStyle({
      style: background === "light" ? Style.Light : Style.Dark,
    }),
    SystemBars.setStyle({
      style:
        background === "light" ? SystemBarsStyle.Light : SystemBarsStyle.Dark,
    }),
  ]);
}

/**
 * Hides the launch splash. Call it once the first real frame is painted, not
 * on module load, or the user sees an empty WebView between the two. Web: a
 * no-op, there is no splash.
 */
export async function hideSplash(): Promise<void> {
  if (!isNative()) {
    return;
  }
  const { SplashScreen } = await import("@capacitor/splash-screen");
  await SplashScreen.hide();
}

export type OrientationLock = "any" | "landscape" | "portrait";

/**
 * Native: the plugin. Web: `screen.orientation.lock`, which most browsers
 * refuse outside fullscreen — the refusal is swallowed and reported as
 * `false`, because a page that cannot lock must still work unlocked.
 */
export async function lockOrientation(lock: OrientationLock): Promise<boolean> {
  try {
    if (!isNative()) {
      const orientation = screen.orientation as ScreenOrientation & {
        lock?: (orientation: string) => Promise<void>;
      };
      if (typeof orientation?.lock !== "function") {
        return false;
      }
      await orientation.lock(lock);
      return true;
    }
    const { ScreenOrientation } = await import("@capacitor/screen-orientation");
    await ScreenOrientation.lock({ orientation: lock });
    return true;
  } catch {
    return false;
  }
}

export async function unlockOrientation(): Promise<void> {
  try {
    if (!isNative()) {
      screen.orientation?.unlock();
      return;
    }
    const { ScreenOrientation } = await import("@capacitor/screen-orientation");
    await ScreenOrientation.unlock();
  } catch {
    // Unlocking something that was never locked is not an error worth a throw.
  }
}
